import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useSuperGetOrganizationsQuery,
  useSuperDeleteOrganizationMutation,
} from '@/features/admin/api/superAdminApi';
import Swal from 'sweetalert2';

export function useSuperOrganizations() {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlanFilter, setSelectedPlanFilter] = useState<'all' | 'freemium' | 'scale' | 'enterprise'>('all');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  
  // Sorting state
  const [sortBy, setSortBy] = useState<'name' | 'created_at'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data: response, isLoading, isFetching, isError, refetch } = useSuperGetOrganizationsQuery({
    page,
    limit,
    search: searchTerm,
    sortBy,
    sortOrder,
    planFilter: selectedPlanFilter
  });
  
  const [deleteOrg] = useSuperDeleteOrganizationMutation();
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  const handleSort = (field: 'name' | 'created_at') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleDeleteClick = (org: any) => {
    Swal.fire({
      title: 'Delete Organization?',
      html: `Are you sure you want to delete <strong>${org.name}</strong>?<br/>This action is irreversible and will permanently delete all associated subscriptions, workspaces, and memberships.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: 'var(--admin-card-subtext)',
      confirmButtonText: 'Yes, delete workspace',
      background: '#0d1117',
      color: '#f0f8ff',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsDeletingId(org.id);
          const res = await deleteOrg(org.id).unwrap();
          if (res.success) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Organization deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#38bdf8',
              background: '#0d1117',
              color: '#f0f8ff',
            });
            refetch();
          }
        } catch (err: any) {
          Swal.fire({
            title: 'Error',
            text: err?.data?.message || 'Failed to delete organization.',
            icon: 'error',
            confirmButtonColor: '#64748b',
            background: '#0d1117',
            color: '#f0f8ff',
          });
        } finally {
          setIsDeletingId(null);
        }
      }
    });
  };

  const organizations = response?.data?.data || [];
  const pagination = response?.data?.pagination || { total: 0, page: 1, limit: 10, totalPages: 0 };

  return {
    searchTerm,
    setSearchTerm,
    selectedPlanFilter,
    setSelectedPlanFilter,
    page,
    setPage,
    limit,
    sortBy,
    sortOrder,
    handleSort,
    handlePageChange,
    handleDeleteClick,
    isDeletingId,
    organizations,
    pagination,
    isLoading,
    isFetching,
    isError,
    navigate
  };
}
